import showNotification from '../components/extras/showNotification';
import { convertXmlToJson } from '../helpers/helpers';
import { store } from '../store/store';
import qs from 'qs';
import { API_GET_VEHICLES } from './api_endpoint';
import { http, httpJson, httpRest, httpwithtoken } from './http';
import { AxiosRequestConfig, AxiosError } from 'axios';

interface IConfigHeaders {
	spacekey: string;
	id: string;
}

export interface IApiResponse<T = unknown> {
	data: T;
	success: boolean;
}

export const configHeaders = (auth?: IConfigHeaders): AxiosRequestConfig => {
	const user = store.getState().auth.user;

	const token = localStorage.getItem('token') ?? '';

	if (auth?.id) {
		return {
			headers: {
				authtoken: token,
				spacekey: auth.spacekey,
				userid: auth.id,
			},
		};
	}

	const {
		user: { spaceKey, id },
	} = user;

	return {
		headers: {
			authtoken: token,
			spacekey: spaceKey,
			userid: id,
		},
	};
};

export const configHeadersIngestion = (): AxiosRequestConfig => ({
	headers: {
		IngestionId: process.env.REACT_APP_INGESTIONID ?? '',
		IngestionSecret: process.env.REACT_APP_INGESTIONSECRET ?? '',
	},
});

export const configHeadersApiRest = (): AxiosRequestConfig => ({
	headers: {
		clientid: process.env.REACT_APP_CLIENT_ID ?? '',
		clientsecret: process.env.REACT_APP_CLIENT_SECRET ?? '',
		appname: process.env.REACT_APP_APP_NAME ?? '',
		'Content-Type': 'application/x-www-form-urlencoded',
	},
});

export const getUrlEndPoint = async (payload: Record<string, unknown>) => {
	try {
		const response = await httpwithtoken.post(
			API_GET_VEHICLES.GET_URL_END_POINT,
			qs.stringify(payload),
			configHeaders(),
		);

		return response;
	} catch (err: unknown) {
		const error = err as AxiosError<any>;

		console.error(error);

		if (error?.response?.status !== 304) {
			showNotification(
				'Server Error',
				error.response?.data?.Error ??
				'Oops! Something went wrong while fetching URL End Point.',
				'danger',
			);
		}
	}
};

export const getRecords = async (payload: Record<string, unknown>, type: string) => {
	const params = new URLSearchParams();

	Object.entries(payload).forEach(([key, value]) => params.append(key, String(value)));

	try {
		const { data } = await http.post(API_GET_VEHICLES.GET_RECORDS, params, configHeaders());

		return convertXmlToJson(data);
	} catch (err: unknown) {
		const error = err as AxiosError<any>;

		console.error(error);

		if (error?.response?.status !== 304) {
			showNotification(
				'Server Error',
				error.response?.data?.Error ?? `Oops! Something went wrong while fetching ${type}.`,
				'danger',
			);
		}
	}
};

export const getData = async <T = unknown>(
	payload: string,
	type: string,
	emptyQuery = false,
): Promise<T> => {
	const { customProperties } = store.getState().auth;
	const fleetId = customProperties.fleetId;
	const fleetIdParsed =
		fleetId && fleetId.includes(',')
			? // eslint-disable-next-line @typescript-eslint/no-shadow
			fleetId.split(',').map((id: string) => Number(id))
			: /^\d+$/.test(fleetId)
				? [Number(fleetId)]
				: ['All Fleets'];
	try {
		let url = payload;

		if (!payload.includes('vin=')) {
			const indexInterrogation = payload.indexOf('?');
			url += `${indexInterrogation !== -1 && indexInterrogation < payload.length - 1 ? '&' : ''
				}vin=${customProperties.vin}`;
		}

		if (!payload.includes('fleet_id=')) {
			url += `&fleet_id=${fleetIdParsed}`;
		}

		if (!payload.includes('organisation_id=')) {
			url += `&organisation_id=${customProperties.organisationId || 2}`;
		}

		const { data } = await httpRest.get(emptyQuery ? payload : url, configHeadersApiRest());

		return data as T;
	} catch (err: unknown) {
		const error = err as AxiosError<any>;

		if (error?.code === 'ERR_CANCELED') {
			throw error; // ← re-throw, don't return []
		}

		console.error('Error fetching data:', error);
		if (error?.response?.data?.success) {
			return [] as T;
		}
		return [] as T;
	}
};

export const dataIngestion = async (payload: Record<string, unknown>) => {
	const ipAddress = store.getState().appStore.ipAddress;

	const {
		user: {
			user: { id, fullName },
		},
		customProperties,
	} = store.getState().auth;
	const fleetId = customProperties.fleetId;
	const fleetIdParsed =
		fleetId && fleetId.includes(',')
			? // eslint-disable-next-line @typescript-eslint/no-shadow
			fleetId.split(',').map((id: string) => Number(id))
			: /^\d+$/.test(fleetId)
				? [Number(fleetId)]
				: ['All Fleets'];

	const org = customProperties.organisationId || 2;

	let organisationId: number[];

	if (typeof org === 'string' && org.includes(',')) {
		organisationId = org.split(',').map((orgId) => Number(orgId.trim()));
	} else {
		organisationId = [Number(org)];
	}

	const common = {
		fm_user_id: id,
		fm_user_name: fullName,
		role: customProperties.role,
		fleet_name: customProperties.fleetName,
		vin: customProperties.vin,
		ip_address: ipAddress,
		datetime: new Date(),
		fleet_id: fleetIdParsed,
		organisation_id: organisationId,
	};

	try {
		// NOTE: `common` holds account-level defaults (e.g. the logged-in user's
		// own vin, which is the placeholder "All Vins" for Admin/fleet-level users).
		// It must be spread FIRST so that any field explicitly provided by the
		// caller in `payload` (e.g. the specific vehicle vin chosen in the
		// Create Task form) wins instead of being silently overwritten.
		const { data } = await httpJson.post(
			API_GET_VEHICLES.DATA_INGESTION,
			{ ...common, ...payload },
			configHeadersIngestion(),
		);

		return data;
	} catch (err: unknown) {
		const error = err as AxiosError<any>;

		console.error('Error in data ingestion:', error);

		if (error?.response?.status !== 304) {
			showNotification(
				'Server Error',
				error.response?.data?.Error ??
				'Oops! Something went wrong while using Data Ingestion API.',
				'danger',
			);
		}
	}
};

export const dataIngestionOnUnload = (payload: Record<string, unknown>) => {
	try {
		const { auth, appStore } = store.getState();
		const { id, fullName } = auth?.user?.user ?? {};

		if (!id) return; // nothing to report if no one is logged in

		const fleetId = auth.customProperties?.fleetId;
		const fleetIdParsed =
			fleetId && fleetId.includes(',')
				? fleetId.split(',').map((fid: string) => Number(fid))
				: /^\d+$/.test(fleetId)
					? [Number(fleetId)]
					: ['All Fleets'];

		const org = auth.customProperties?.organisationId || 2;
		const organisationId =
			typeof org === 'string' && org.includes(',')
				? org.split(',').map((orgId) => Number(orgId.trim()))
				: [Number(org)];

		const body = JSON.stringify({
			...payload,
			fm_user_id: id,
			fm_user_name: fullName,
			role: auth.customProperties?.role,
			fleet_name: auth.customProperties?.fleetName,
			vin: auth.customProperties?.vin,
			ip_address: appStore?.ipAddress,
			datetime: new Date(),
			fleet_id: fleetIdParsed,
			organisation_id: organisationId,
		});

		const url = `${process.env.REACT_APP_SERVICE_URL_INGESTION}/ingestion/dataIngestion`;

		fetch(url, {
			method: 'POST',
			keepalive: true,
			headers: {
				'Content-Type': 'application/json',
				IngestionId: process.env.REACT_APP_INGESTIONID ?? '',
				IngestionSecret: process.env.REACT_APP_INGESTIONSECRET ?? '',
			},
			body,
		}).catch((err) => console.error('Error sending logout beacon:', err));
	} catch (err) {
		console.error('Error preparing logout beacon:', err);
	}
};

export const dataIngestionAdmin = async (payload: Record<string, unknown>) => {
	const ipAddress = store.getState().appStore.ipAddress;

	const {
		user: {
			user: { id, fullName },
		},
		customProperties,
	} = store.getState().auth;
	const fleetId = customProperties.fleetId;
	const fleetIdParsed =
		fleetId && fleetId.includes(',')
			? // eslint-disable-next-line @typescript-eslint/no-shadow
			fleetId.split(',').map((id: string) => Number(id))
			: /^\d+$/.test(fleetId)
				? [Number(fleetId)]
				: ['All Fleets'];

	const org = customProperties.organisationId || 2;
	let organisationId: number[];
	if (typeof org === 'string' && org.includes(',')) {
		organisationId = org.split(',').map((orgId) => Number(orgId.trim()));
	} else {
		organisationId = [Number(org)];
	}

	const common = {
		fm_user_id: id,
		fm_user_name: fullName,
		role: customProperties.role,
		fleet_name: customProperties.fleetName,
		vin: customProperties.vin,
		ip_address: ipAddress,
		datetime: new Date(),
		fleet_id: fleetIdParsed,
		organisation_id: organisationId,
	};

	try {
		// NOTE: `common` holds account-level defaults (e.g. the logged-in user's
		// own vin, which is the placeholder "All Vins" for Admin/fleet-level
		// users). It must be spread FIRST so that any field explicitly provided
		// by the caller in `payload` (e.g. the specific row's vin in the Setup
		// Admin > Vehicles edit drawer) wins instead of being silently
		// overwritten. See dataIngestion() above for the same pattern.
		const { data } = await httpJson.post(
			API_GET_VEHICLES.DATA_INGESTION,
			{ ...common, ...payload },
			{
				headers: {
					IngestionId: process.env.REACT_APP_INGESTIONID_Admin ?? '',
					IngestionSecret: process.env.REACT_APP_INGESTIONSECRET_Admin ?? '',
				},
			},
		);
		return data;
	} catch (err: unknown) {
		const error = err as AxiosError<any>;
		console.error('Error in admin data ingestion:', error);
		if (error?.response?.status !== 304) {
			showNotification(
				'Server Error',
				error.response?.data?.Error ?? 'Oops! Something went wrong while using Admin Ingestion API.',
				'danger',
			);
		}
	}
};